import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Joyride,
  type Step,
  type Controls,
  type EventData,
} from "react-joyride";
import { apiRequest } from "@/lib/queryClient";
import { StepContent } from "./StepContent";
import {
  TourTableOfContents,
  TOC_MIN_VISIBLE_STEPS,
} from "./TourTableOfContents";
import { useTourContext } from "./TourContext";
import { isInTourPreview } from "./TourPreviewHighlighter";

function postQuiet(path: string, body?: unknown): void {
  apiRequest("POST", path, body).catch(() => undefined);
}

// P23C: Animated wrapper for step content. Remounts on stepKey change,
// retriggering the CSS entrance animation defined in index.css.
function StepAnimationShell({
  stepKey,
  children,
}: {
  stepKey: string;
  children: React.ReactNode;
}) {
  const [cls, setCls] = useState("tour-step-enter");
  useEffect(() => {
    // Force re-animation on step change: clear then re-add the class.
    setCls("");
    const raf = requestAnimationFrame(() => setCls("tour-step-enter"));
    return () => cancelAnimationFrame(raf);
  }, [stepKey]);
  return <div className={cls}>{children}</div>;
}

export function TourManager() {
  const { tour, steps, isRunning, markCompleted, runId } = useTourContext();
  const controlsRef = useRef<Controls | null>(null);
  // One-shot guard: ensures we POST /seen or /dismiss exactly once per
  // run, even if Joyride emits multiple terminal-status events. Reset
  // each time a fresh run starts.
  const terminalPostedRef = useRef(false);

  useEffect(() => {
    if (isRunning) {
      terminalPostedRef.current = false;
    }
  }, [isRunning]);

  // Lock body scroll while a tour is running. Joyride V3 computes
  // the spotlight cutout once per step and does not re-position it
  // when the user scrolls the page, so a stray hover-and-scroll
  // leaves the highlight stranded above stale content. The tour is
  // already a modal experience (overlay, X / Skip / Esc dismiss
  // path), so disabling scroll matches that contract. The
  // scrollbar-width padding keeps the page from reflowing when the
  // scrollbar disappears.
  useEffect(() => {
    if (!isRunning) return;
    const body = document.body;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    const previousOverflow = body.style.overflow;
    const previousPaddingRight = body.style.paddingRight;
    body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }
    return () => {
      body.style.overflow = previousOverflow;
      body.style.paddingRight = previousPaddingRight;
    };
  }, [isRunning]);

  const handleJump = useCallback((index: number) => {
    controlsRef.current?.go(index);
  }, []);

  // P23C: highlight ordinal per step (chapters get null — no counter shown).
  const { tocSteps, highlightCounters, totalHighlights } = useMemo(() => {
    const toc = steps.map((s) => ({
      id: s.id,
      title: s.title,
      stepType: s.stepType ?? "highlight",
    }));
    let ord = 0;
    const counters = steps.map((s) => {
      if ((s.stepType ?? "highlight") === "chapter") return null;
      return ++ord;
    });
    const total = steps.filter((s) => (s.stepType ?? "highlight") !== "chapter").length;
    return { tocSteps: toc, highlightCounters: counters, totalHighlights: total };
  }, [steps]);

  const joyrideSteps: Step[] = useMemo(() => {
    return steps.map((s, i) => {
      const isChapter = (s.stepType ?? "highlight") === "chapter";
      const highlightOrdinal = highlightCounters[i];

      // P23C: chapter steps show the TOC trigger but no step counter.
      // Highlight steps show both ordinal and TOC (when threshold is met).
      const tocEl = totalHighlights >= TOC_MIN_VISIBLE_STEPS ? (
        <TourTableOfContents
          steps={tocSteps}
          currentIndex={i}
          onJump={handleJump}
        />
      ) : null;

      const headerSlot = isChapter ? tocEl : (
        <>
          {highlightOrdinal !== null ? (
            <span
              className="text-xs text-muted-foreground"
              data-testid={`text-tour-step-counter-${i}`}
            >
              Step {highlightOrdinal} of {totalHighlights}
            </span>
          ) : null}
          {tocEl}
        </>
      );

      // P23C: chapter steps target "body" with center placement.
      // Highlight steps resolve their DOM anchor normally.
      const target = isChapter
        ? "body"
        : (s.targetSelector ?? `[data-tour="${s.targetId}"]`);
      const placement = isChapter ? "center" : s.placement;

      // P23a: per-step pre-action (not applicable for chapter steps).
      const pre = isChapter ? null : s.preAction;
      const before = pre
        ? async () => {
            try {
              const target = document.querySelector(pre.selector);
              if (target instanceof HTMLElement) {
                target.click();
                if (pre.settleMs > 0) {
                  await new Promise((r) => setTimeout(r, pre.settleMs));
                }
              }
            } catch {
              // swallow — Joyride's beforeTimeout will eventually
              // resolve even if we throw, but keeping it quiet
              // avoids a console error on a malformed selector.
            }
          }
        : undefined;

      // P23C: chapter steps get a wider tooltip so the centered card
      // has more breathing room. Joyride V3 supports per-step styles.
      // tooltipContent.paddingTop is zeroed so the chapter header row
      // can be pulled flush with Joyride's absolutely-positioned X button
      // (which sits at top:0, right:0 of the tooltip — 12px above where
      // the content area normally begins).
      const stepStyles = isChapter
        ? {
            tooltip: { width: "520px", maxWidth: "90vw" },
            tooltipContent: { paddingTop: 0, paddingBottom: 12 },
          }
        : undefined;

      return {
        target,
        placement,
        skipBeacon: true,
        before,
        styles: stepStyles,
        content: (
          <StepAnimationShell stepKey={`${tour?.id ?? ""}:${runId}:${i}`}>
            <StepContent
              title={s.title}
              body={s.body}
              mediaUrl={s.mediaUrl}
              headerSlot={headerSlot}
              isChapter={isChapter}
            />
          </StepAnimationShell>
        ),
      };
    });
  }, [steps, tocSteps, highlightCounters, totalHighlights, handleJump, tour, runId]);

  const handleEvent = useCallback(
    (data: EventData, controls: Controls) => {
      controlsRef.current = controls;

      // G6: We are the sole scroll authority for tour playback.
      // Joyride's own scroll is disabled via `options.skipScroll: true`
      // below, so this handler runs without competing
      // with Joyride's internal scroll pipeline (which only fires
      // reliably for the first step and can fight nested scroll
      // parents on later steps). Synchronous-style `behavior: "auto"`
      // is intentional: Joyride's placement calculation runs in the
      // tooltip lifecycle right after this event, and we need the
      // target's post-scroll coordinates to be stable by then.
      // P23C: chapter steps skip scroll (they target "body" — no DOM
      // element to scroll into view).
      if (data.type === "step:before") {
        const step = joyrideSteps[data.index];
        const selector = typeof step?.target === "string" ? step.target : null;
        const isChapterTarget = selector === "body";
        if (selector && !isChapterTarget) {
          // P23a: with manual CSS selectors in the mix, a malformed
          // selector that slipped past UI validation must not throw
          // a SyntaxError up through Joyride's lifecycle and abort
          // tour playback. The missing-target case is already a
          // silent skip elsewhere; treat parse-error the same way.
          try {
            const el = document.querySelector(selector);
            if (el instanceof HTMLElement) {
              el.scrollIntoView({ block: "center", behavior: "auto" });
            }
          } catch {
            // ignore — Joyride will continue and either find the
            // element via its own resolver or skip the step.
          }
        }
      }

      // G12: Close (X) click and Esc keypress both emit
      // data.action === "close" in Joyride V3. The "tour:end" event
      // that follows carries status="paused" (not "skipped"), so the
      // status-only branch below misses it and the dismiss never
      // persists. Treat any close action as an explicit dismiss
      // before falling through to progress / terminal handling.
      if (data.action === "close" && tour && !terminalPostedRef.current) {
        terminalPostedRef.current = true;
        markCompleted("dismiss");
        return;
      }

      if (data.type === "step:after" && tour) {
        postQuiet(`/api/tours/${tour.id}/progress`, { stepIndex: data.index });
      }

      // Terminal handling — gated to the dedicated TOUR_END event AND a
      // one-shot ref so duplicate Joyride events with the same terminal
      // status cannot double-post.
      if (data.type !== "tour:end") return;
      if (terminalPostedRef.current) return;
      if (!tour) return;

      if (data.status === "finished") {
        terminalPostedRef.current = true;
        markCompleted("seen");
      } else if (data.status === "skipped") {
        terminalPostedRef.current = true;
        markCompleted("dismiss");
      }
    },
    [tour, markCompleted, joyrideSteps],
  );

  // In preview mode (admin step editor iframe), normal tour playback is
  // suppressed; TourPreviewHighlighter draws a static ring instead. Placed
  // after all hooks above to preserve hook order across renders.
  if (isInTourPreview()) return null;
  if (!tour || steps.length === 0) return null;

  return (
    <Joyride
      // V2 follow-up: keying on (tour.id, runId) forces a fresh
      // Joyride mount on every trigger (autostart or help
      // button) so re-runs always start at step 0. Joyride V3's
      // `run` prop alone does not reset its internal step index,
      // which previously caused dismiss → re-trigger to resume
      // mid-tour instead of restarting. tour.id changes when the
      // server picks a different tour for the new path; runId
      // bumps on every fresh start within the same tour. The
      // runId reset on path change (in useTour) closes the brief
      // stale-key window the V2 fix called out — by the time a
      // new tour mounts, runId is already 0, then autostart bumps
      // it to 1, so the key strictly differs from the previous
      // tour's last key.
      key={`${tour.id}:${runId}`}
      run={isRunning}
      steps={joyrideSteps}
      continuous
      locale={{ last: "Done" }}
      onEvent={handleEvent}
      options={{
        backgroundColor: "hsl(var(--popover))",
        textColor: "hsl(var(--popover-foreground))",
        arrowColor: "hsl(var(--popover))",
        primaryColor: "hsl(var(--primary))",
        overlayColor: "rgba(0, 0, 0, 0.55)",
        zIndex: 100,
        // V3 prop migration: in V2 these were top-level Joyride
        // props; V3 moved them into Options.
        // - `showSkipButton` → buttons array (V3 default omits 'skip')
        // - `disableOverlayClose` → overlayClickAction: false
        // - `disableScrolling` → skipScroll: true (G6: TourManager
        //   owns scroll via the step:before handler above so
        //   Joyride's built-in scroll must stay disabled)
        buttons: ["back", "close", "primary", "skip"],
        overlayClickAction: false,
        skipScroll: true,
      }}
      styles={{
        tooltip: {
          backgroundColor: "hsl(var(--popover))",
          color: "hsl(var(--popover-foreground))",
          borderRadius: "0.5rem",
          // G8: Drop the 1px border in favour of a soft shadow.
          // The border created a visible seam where the arrow met the
          // bubble (the arrow doesn't carry the border line). Shadow
          // gives sufficient definition against the dark overlay
          // without the seam artifact.
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        },
        tooltipContainer: {
          textAlign: "left",
        },
        tooltipContent: {
          color: "hsl(var(--popover-foreground))",
        },
        buttonPrimary: {
          backgroundColor: "hsl(var(--primary))",
          color: "hsl(var(--primary-foreground))",
          borderRadius: "0.375rem",
          padding: "0.375rem 0.75rem",
          fontSize: "0.875rem",
        },
        buttonBack: {
          color: "hsl(var(--muted-foreground))",
          marginRight: "0.5rem",
          fontSize: "0.875rem",
        },
        buttonClose: {
          color: "hsl(var(--muted-foreground))",
        },
        buttonSkip: {
          color: "hsl(var(--muted-foreground))",
          fontSize: "0.875rem",
        },
      }}
    />
  );
}
